"use client"

import { FooterProps } from "@/(admin)/modules/ui-builder/config/themes/tamda-media/TamdaMediaFooter/RenderConfig"
import { ADMIN_API_CONFIG } from "@/constants/api-configs"
import { Images } from "@/constants/images"
import { useTamdaMediaSettings } from "@/hook/useTamdaMediaSettings"
import { linkCategoryPath } from "@/lib/config/lib/helper"
import { usePostIdStore } from "@/stores/use-post-id-store"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { twMerge } from "tailwind-merge"

const Footer = ({
  logoImage,
  slogan,
  pagesColumn1,
  pagesColumn2,
  pagesColumn3,
  pagesColumn4,
  phone,
  email,
  advertisingContact,
  facebookIcon,
  facebookUrl,
  youtubeIcon,
  youtubeUrl,
  copyrightText,
  className,
}: FooterProps) => {
  const { settings } = useTamdaMediaSettings()
  const pathname = usePathname()

  const logoUrl = logoImage ? `${ADMIN_API_CONFIG.API_URL}${logoImage}` : ""
  const facebookIconUrl = facebookIcon ? `${ADMIN_API_CONFIG.API_URL}${facebookIcon}` : ""
  const youtubeIconUrl = youtubeIcon ? `${ADMIN_API_CONFIG.API_URL}${youtubeIcon}` : ""

  const displayPhone = settings?.phone || phone
  const displayEmail = settings?.email || email
  const displayAdvertisingContact = settings?.advertisingEmail || advertisingContact
  const displayFacebookUrl = settings?.facebook || facebookUrl
  const displayYoutubeUrl = settings?.youtube || youtubeUrl
  const displaySlogan = settings?.tagline || slogan

  const postDataMap = usePostIdStore(state => state.postDataMap)

  // const hasEditPermission = (() => {
  //   if (typeof window === "undefined") return false
  //   try {
  //     const permissions = JSON.parse(localStorage.getItem("permissions") || "{}")
  //     const canEditPost = permissions.post?.update?.enabled === true
  //     const canEditCategory = permissions.category?.update?.enabled === true
  //     return canEditPost || canEditCategory
  //   } catch {
  //     return false
  //   }
  // })()

  const segments = pathname?.split("/").filter(Boolean) || []
  const slug = segments.join("/")
  const postId = postDataMap[slug]?.id || null

  const showEditIcon = postId !== null
  //  hasEditPermission

  return (
    <footer className={twMerge("bg-[#F7F7F7] text-[#333] border-t border-[#E5E5EA]", className)}>
      <div className="container mx-auto w-full max-w-[1128px] px-4 min-[1180px]:px-0 min-[1180px]:w-[1128px]">
        {/* Top Section: Logo và Slogan */}
        <div className="min-[1180px]:pt-12 min-[1180px]:pb-5 text-center pt-16 pb-4">
          <div className="flex flex-col gap-2.5 items-center">
            <Link href="/" className="flex justify-center relative w-[170px] min-[1180px]:w-[200px] h-[34.41px]">
              <Image
                src={logoUrl || Images.logoTamdaMedia}
                alt="TamdaMedia Logo"
                fill
                className="h-auto w-auto object-contain"
                priority
              />
            </Link>

            {displaySlogan && (
              <p className="text-[#333333] min-[1180px]:p-0 font-bold min-[1180px]:text-sm uppercase text-sm lg:leading-[21.7px] px-6">
                {displaySlogan}
              </p>
            )}
          </div>
        </div>

        {/* Middle Section: Mobile 2 cột x 3 hàng, Desktop 5 cột */}
        <div className="pt-8 lg:pb-[52px] min-[1180px]:pt-[27px] min-[1180px]:pb-12">
          {/* Mobile: 2 cột x 3 hàng */}
          <div className="grid grid-cols-2 gap-y-8 gap-x-4 min-[1180px]:hidden">
            {/* Hàng 1 - Cột trái: pagesColumn1 (2 item đầu) */}
            <div className="w-fit">
              <ul className="flex flex-col gap-3">
                {pagesColumn1?.slice(0, 2).map((item, index) => (
                  <li key={index}>
                    <Link
                      href={linkCategoryPath(item.url || "#")}
                      className="text-[#000000CC] text-base font-bold hover:text-[#1EAD46] transition-colors cursor-pointer"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Hàng 1 - Cột phải: pagesColumn2 */}
            <div className="w-fit">
              <ul className="flex flex-col gap-3">
                {pagesColumn2?.map((item, index) => (
                  <li key={index}>
                    <Link
                      href={linkCategoryPath(item.url || "#")}
                      className="text-[#000000CC] text-base leading-[21.7px] font-medium hover:text-[#1EAD46] transition-colors cursor-pointer"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Hàng 2 - Cột trái: pagesColumn3 */}
            <div className="w-fit">
              <ul className="flex flex-col gap-3">
                {pagesColumn3?.map((item, index) => (
                  <li key={index}>
                    <Link
                      href={linkCategoryPath(item.url || "#")}
                      className="text-[#000000CC] text-base leading-[21.7px] font-medium hover:text-[#1EAD46] transition-colors cursor-pointer"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Hàng 2 - Cột phải: pagesColumn4 */}
            <div className="w-fit">
              <ul className="flex flex-col gap-3">
                {pagesColumn4?.map((item, index) => (
                  <li key={index}>
                    <Link
                      href={linkCategoryPath(item.url || "#")}
                      className="text-[#000000CC] text-base leading-[21.7px] font-medium hover:text-[#1EAD46] transition-colors cursor-pointer"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Hàng 3 - Cột trái: Contact */}
            <div className="w-fit">
              <div className="pl-4 flex flex-col gap-3">
                {displayPhone && (
                  <div>
                    <p className="text-[#333] text-sm leading-[21.7px]">
                      <span className="font-bold">Hotline: </span>
                      <span className="font-extrabold text-sm hover:text-[#1EAD46] transition-colors cursor-pointer">
                        {displayPhone}
                      </span>
                    </p>
                  </div>
                )}
                {displayEmail && (
                  <div>
                    <p className="text-[#333] text-sm leading-[21.7px]">
                      <span className="font-bold">Email: </span>
                      <Link
                        href={`mailto:${displayEmail}`}
                        className="text-[#008746] font-extrabold hover:text-[#1EAD46] transition-colors cursor-pointer"
                      >
                        {displayEmail}
                      </Link>
                    </p>
                  </div>
                )}
                {displayAdvertisingContact && (
                  <div>
                    <p className="text-[#333] text-sm leading-[21.7px]">
                      <span className="font-bold">Liên hệ quảng cáo: </span>
                      <Link
                        href={`mailto:${displayAdvertisingContact}`}
                        className="text-[#008746] font-extrabold hover:text-[#1EAD46] transition-colors cursor-pointer"
                      >
                        {displayAdvertisingContact}
                      </Link>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Hàng 3 - Cột phải: để trống */}
            <div className="w-fit" />
          </div>

          {/* Desktop: 5 cột như cũ */}
          <div className="hidden min-[1180px]:grid min-[1180px]:[grid-template-columns:20.83%_16.67%_16.67%_16.67%_29.18%] max-w-7xl mx-auto justify-start">
            {/* Cột 1 */}
            <div className="w-fit">
              <ul className="flex flex-col gap-3 pl-2">
                {pagesColumn1?.map((item, index) => (
                  <li key={index}>
                    <Link
                      href={linkCategoryPath(item.url || "#")}
                      className="text-[#000000CC] text-base leading-[24.8px] font-bold hover:text-[#1EAD46] transition-colors cursor-pointer"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Cột 2 */}
            <div className="w-fit">
              <ul className="flex flex-col gap-3 pl-2">
                {pagesColumn2?.map((item, index) => (
                  <li key={index}>
                    <Link
                      href={linkCategoryPath(item.url || "#")}
                      className="text-[#000000CC] text-base leading-[24.8px] font-medium hover:text-[#1EAD46] transition-colors cursor-pointer"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Cột 3 */}
            <div className="w-fit">
              <ul className="flex flex-col gap-3 pl-2">
                {pagesColumn3?.map((item, index) => (
                  <li key={index}>
                    <Link
                      href={linkCategoryPath(item.url || "#")}
                      className="text-[#000000CC] text-base leading-[24.8px] font-medium hover:text-[#1EAD46] transition-colors cursor-pointer"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Cột 4 */}
            <div className="w-fit">
              <ul className="flex flex-col gap-3 pl-2">
                {pagesColumn4?.map((item, index) => (
                  <li key={index}>
                    <Link
                      href={linkCategoryPath(item.url || "#")}
                      className="text-[#000000CC] text-base leading-[24.8px] font-medium hover:text-[#1EAD46] transition-colors cursor-pointer"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Cột 5: Contact Info */}
            <div className="w-fit">
              <div className="flex flex-col gap-3 pl-2">
                {displayPhone && (
                  <div>
                    <p className="text-[#333] text-sm leading-[21.7px]">
                      <span className="font-bold">Hotline: </span>
                      <span className="font-extrabold text-sm leading-[21.7px] hover:text-[#1EAD46] transition-colors cursor-pointer">
                        {displayPhone}
                      </span>
                    </p>
                  </div>
                )}
                {displayEmail && (
                  <div>
                    <p className="text-[#333] text-sm leading-[21.7px]">
                      <span className="font-bold">Email: </span>
                      <Link
                        href={`mailto:${displayEmail}`}
                        className="text-[#008746] text-sm leading-[21.7px] font-extrabold hover:text-[#1EAD46] transition-colors cursor-pointer"
                      >
                        {displayEmail}
                      </Link>
                    </p>
                  </div>
                )}
                {displayAdvertisingContact && (
                  <div>
                    <p className="text-[#333] text-sm leading-[21.7px]">
                      <span className="font-bold">Liên hệ quảng cáo: </span>
                      <Link
                        href={`mailto:${displayAdvertisingContact}`}
                        className="text-[#008746] text-sm leading-[21.7px] font-extrabold hover:text-[#1EAD46] transition-colors cursor-pointer"
                      >
                        {displayAdvertisingContact}
                      </Link>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section: full-width border, content constrained inside */}
      <div className="border-t border-gray-200">
        <div className="container mx-auto w-full flex max-w-[1128px] px-4 min-[1180px]:px-0 min-[1180px]:w-[1128px] pt-4 pb-9 sm:pt-0 sm:pb-0 min-[1180px]:h-[70px]">
          <div className="flex flex-col w-full min-[1180px]:flex-row justify-between items-center min-[1180px]:items-center gap-4">
            {/* Copyright Text */}
            <div className="text-xs min-[1180px]:text-sm leading-[1.1rem] min-[1180px]:leading-6 text-[#333333] font-normal text-center min-[1180px]:text-left flex-1 order-1 min-[1180px]:order-1 py-0">
              {copyrightText && (
                <div className="space-y-0">
                  {copyrightText.split("\n").map((line, index) => (
                    <p key={index} className="leading-[21.7px] text-sm">
                      {line}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Social Icons - On right for desktop */}
            <div className="flex gap-[12.8px] h-[38.8px] order-2 min-[1180px]:order-2">
              {displayFacebookUrl && (
                <Link
                  href={displayFacebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center relative w-[2rem] h-[2rem]"
                >
                  {facebookIconUrl ? (
                    <Image
                      src={Images.facebookIcon}
                      alt="Facebook"
                      fill
                      className="object-contain rounded-full w-[2rem] h-[2rem]"
                    />
                  ) : (
                    <Image
                      src={facebookIconUrl}
                      alt="Facebook"
                      fill
                      className="object-contain rounded-full w-[2rem] h-[2rem]"
                    />
                  )}
                </Link>
              )}
              {displayYoutubeUrl && (
                <Link
                  href={displayYoutubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center relative w-[2rem] h-[2rem]"
                >
                  {youtubeIconUrl ? (
                    <Image
                      src={Images.youtubeIconHeader}
                      alt="Youtube"
                      fill
                      className="object-contain rounded-full w-[2rem] h-[2rem]"
                    />
                  ) : (
                    <Image
                      src={youtubeIconUrl}
                      alt="Youtube"
                      fill
                      className="object-contain rounded-full w-[2rem] h-[2rem]"
                    />
                  )}
                </Link>
              )}
              {showEditIcon && postId && (
                <Link
                  href={`/admin/posts/edit?id=${postId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex relative"
                >
                  <Image src={Images.editIcon} alt="Edit" width={28} height={28} className="w-[28px] h-[28px]" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
